import { useEffect, useMemo, useRef } from "react";
import { useAppAuth } from "~/lib/auth/app-auth";
import { useCanonicalUserIdentity } from "~/lib/auth/canonical-user-identity";
import {
  isWebappV2FrontendTelemetryEnabled,
  isWebappV2ProductAnalyticsEnabled,
  isWebappV2SessionReplayEnabled,
} from "~/lib/env";
import { getRouteVisitId } from "~/lib/route-visit";
import type { AppRouterContext } from "~/lib/router-runtime";
import { SentryIdentityController } from "~/lib/sentry-identity-controller";
import {
  identifyTelemetryUser,
  setTelemetryContext,
  trackTelemetryEvent,
} from "./client";
import { enableGa4Telemetry } from "./providers/ga4";
import { enablePostHogTelemetry } from "./providers/posthog";
import { createCanonicalTelemetryIdentity } from "./identity";
import { toRouteTelemetryPath } from "./router";

export interface TelemetryRuntimeControllerProps {
  readonly context: AppRouterContext;
  readonly pathname: string;
}

export function TelemetryRuntimeController(
  props: TelemetryRuntimeControllerProps,
) {
  const auth = useAppAuth();
  const routePath = useMemo(
    () => toRouteTelemetryPath(props.pathname),
    [props.pathname],
  );
  const frontendTelemetryEnabled = isWebappV2FrontendTelemetryEnabled();
  const productAnalyticsEnabled =
    frontendTelemetryEnabled && isWebappV2ProductAnalyticsEnabled();
  const sessionReplayEnabled =
    productAnalyticsEnabled && isWebappV2SessionReplayEnabled();
  const pageViewKeyRef = useRef<string | null>(null);
  const authStateKeyRef = useRef<string | null>(null);
  const canonicalIdentityQuery = useCanonicalUserIdentity(props.context, {
    enabled: frontendTelemetryEnabled,
  });
  const canonicalUserId =
    auth.ready && auth.authenticated
      ? (canonicalIdentityQuery.data?.userId ?? null)
      : null;

  useEffect(() => {
    setTelemetryContext({
      auth_source: auth.source,
      authenticated: auth.authenticated,
      route_visit_id: getRouteVisitId(),
      route_id: routePath,
      route_path: routePath,
      vm_preference: props.context.auth.vmPreference,
    });
  }, [
    auth.authenticated,
    auth.source,
    props.context.auth.vmPreference,
    routePath,
  ]);

  useEffect(() => {
    identifyTelemetryUser(
      createCanonicalTelemetryIdentity({
        authenticated: auth.authenticated,
        authSource: auth.source,
        canonicalUserId,
        providerUserId: auth.transport.userId,
        ready: auth.ready,
        walletAddress: auth.transport.walletAddress,
      }),
    );
  }, [
    auth.authenticated,
    auth.ready,
    auth.source,
    auth.transport.userId,
    auth.transport.walletAddress,
    canonicalUserId,
  ]);

  useEffect(() => {
    if (!productAnalyticsEnabled) {
      return;
    }

    enableGa4Telemetry();
    enablePostHogTelemetry({
      sessionReplayEnabled,
    });
    // TODO(X Ads): wire the webapp2 pixel only after marketing confirms the v2
    // pixel ID, event IDs, event names, and payloads. Do not assume webapp1 IDs
    // apply to the replacement app.
  }, [productAnalyticsEnabled, sessionReplayEnabled]);

  useEffect(() => {
    if (!frontendTelemetryEnabled) {
      return;
    }

    const pageViewKey = [
      routePath,
      auth.source,
      String(auth.authenticated),
      props.context.auth.vmPreference,
    ].join(":");
    if (pageViewKeyRef.current === pageViewKey) {
      return;
    }

    pageViewKeyRef.current = pageViewKey;
    trackTelemetryEvent("frontend.route.viewed", {
      auth_source: auth.source,
      authenticated: auth.authenticated,
      route_visit_id: getRouteVisitId(),
      route_id: routePath,
      route_path: routePath,
      vm_preference: props.context.auth.vmPreference,
    });
  }, [
    auth.authenticated,
    auth.source,
    frontendTelemetryEnabled,
    props.context.auth.vmPreference,
    routePath,
  ]);

  useEffect(() => {
    if (!frontendTelemetryEnabled || !auth.ready) {
      return;
    }

    const authStateKey = [
      auth.source,
      String(auth.authenticated),
      props.context.auth.vmPreference,
    ].join(":");
    if (authStateKeyRef.current === authStateKey) {
      return;
    }

    authStateKeyRef.current = authStateKey;
    trackTelemetryEvent("frontend.auth.state.changed", {
      auth_source: auth.source,
      authenticated: auth.authenticated,
      route_visit_id: getRouteVisitId(),
      vm_preference: props.context.auth.vmPreference,
    });
  }, [
    auth.authenticated,
    auth.ready,
    auth.source,
    frontendTelemetryEnabled,
    props.context.auth.vmPreference,
  ]);

  return <SentryIdentityController context={props.context} />;
}
